/**
 * Quality Triage shell — the reimagined Quality Co-Pilot door (review 3ofroc).
 *
 * The door is triage-first: a light tab bar (Triage / Cases / Done, then
 * "More ▾" holding Routed away and Daily briefs) with alert badges, and the
 * queue as the landing. Items waiting on someone else are not a tab (the
 * owner, waiting filter round: "there should just be the ability to filter
 * based on it, and a notification if someone has responded"): they stay in
 * Triage and Cases, each list filters to them, and a "responded" badge on
 * the list's tab says when the person answered. A general question for Merlin
 * starts from the "Ask Merlin" button beside the search (tabs round: the
 * "Ask Merlin" tab was redundant with every item's own Merlin pane).
 * One shared 30s poll (useQcpInvestigations) feeds the badges and every tab
 * via the Outlet context, so the counts and the pages never disagree.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { QualityReadOnlyContext } from '../../context/QualityReadOnlyContext';
import { isQualityViewer } from '../../utils/qualityViewer';
import { useQcpInvestigations } from '../../components/portal/useQcpInvestigations';
import { openAskKey, useRefreshCasesOnAskChange } from './askCourtSync';
import { useQcpDataRequests } from '../../components/portal/useQcpDataRequests';
import MerlinMark from '../../components/portal/MerlinMark';
import { MERLINS } from '../../constants/merlins';
import { queueLanes, routedAwayThreads, tabAlerts } from './qualityTriageView';
import { QualitySearch } from './QualitySearch';
import { QualityMoreMenu, type MoreEntry } from './QualityMoreMenu';
import { undeliveredCount } from './deliveryView';
import { changedSinceWords } from './stateWords';
import { useDeliveryOutcomes } from './useDeliveryOutcomes';
import { useThreadTimelines } from './useThreadTimelines';
import { responseNotices } from './respondedView';
import { answerRow, answersForYou } from './answersView';
import { AnswerDrawer } from '../../components/quality/AnswerDrawer';
import type { QualityTriageContext } from './qualityTriageContext';
import { isWorkspacePath, originFromState } from './qualityWorkspaceView';
import { ShortcutLegend } from './ShortcutLegend';
import { useDoorShortcuts, type DoorTab } from './useDoorShortcuts';
import { trackEvent } from '../../services/portalTracking';
import { useDoorDesk } from './useDoorDesk';
import { deskQueueName } from './deskOwnerView';
import { DeskOwnerLine } from './DeskOwnerLine';
import { draftItems, draftQuestionCount } from './draftsView';
import { TeamBriefLine } from './TeamBriefLine';
import { useMerlinFollowups } from './useMerlinFollowups';
import { useFoundAnswers } from './useFoundAnswers';
import { followupSeenSnapshot, openFollowups, unseenAnswerCount } from './pendingView';
import styles from './QualityTriageShell.module.css';

export default function QualityTriageShell() {
  const { user, hasScope } = useAuth();
  const userId = user?.id ?? null;
  // Whose queue this is: the viewer's own, or for an admin off the quality
  // team the desk's home person (proposal 12 E2E). Every lane, badge and
  // "for <name>" line below reads this one name.
  const desk = useDoorDesk(!!userId);
  const userName = deskQueueName(desk, user?.name ?? null);
  // The read-only Quality view (kanban 808dabf6): the Sigma team and the
  // executives see this desk exactly as Jess does and change nothing — every
  // write control inside the door asks useQualityReadOnly() and stays out of
  // the DOM; the API refuses the same writes.
  const readOnly = isQualityViewer(user);
  const invState = useQcpInvestigations(!!userId);
  const dataRequests = useQcpDataRequests(!!userId);
  // An ask sent, answered or withdrawn moves its case between courts on the
  // server: re-read the cases then, not at the next 30 s poll.
  useRefreshCasesOnAskChange(openAskKey(dataRequests.requests), invState.refresh);
  // The follow-ups Merlin promised (kanban 43160d8b): one poll for the
  // Pending tab's count, its rows and the item's "Merlin will check back" line.
  const followups = useMerlinFollowups(!!userId);
  // Answers the daily sweep found elsewhere (kanban 3eed0be5): one poll for
  // Triage's "Merlin found answers" strip.
  const foundAnswers = useFoundAnswers(!!userId);
  const navigate = useNavigate();
  // The shell owns the scrolling, so a tab kept the previous tab's scroll
  // position (turn 4: Cases opened halfway down). Each new page starts at
  // the top.
  const { pathname, state } = useLocation();
  const contentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0 });
  }, [pathname]);
  // Seen-marks live in localStorage; bumping this tick after markThreadSeen
  // makes the badge memo below re-read them.
  const [seenTick, setSeenTick] = useState(0);

  // The asks count too: an item with an open ask waits on whoever was asked,
  // so it is not in the Triage tab's "your call" number.
  const askList = dataRequests.requests;
  const alerts = useMemo(
    () => tabAlerts(invState.threads, invState.investigations, userName, undefined, askList ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seenTick re-reads localStorage seen-marks
    [invState.threads, invState.investigations, userName, seenTick, askList],
  );

  // Whether each routed-away send arrived: read here, not only on Routed
  // away, so "More" can raise the alarm while that page is out of sight.
  const routedAway = useMemo(
    () => routedAwayThreads(invState.threads, userName),
    [invState.threads, userName],
  );
  const deliveryOutcomes = useDeliveryOutcomes(routedAway);
  const undelivered = undeliveredCount(routedAway, deliveryOutcomes);

  // Has anyone answered? Each of my items waiting on a person is checked
  // against its asks and its timeline (read once per revision; the 30s list
  // poll moves updated_at when the item changes), plus every ask answered
  // and not yet opened. One reading for the badges and the pages.
  const waitingMine = useMemo(
    () => queueLanes(invState.threads, userName, askList ?? []).waiting,
    [invState.threads, userName, askList],
  );
  const waitTimelines = useThreadTimelines(waitingMine);
  const requests = dataRequests.requests;
  const responses = useMemo(
    () =>
      dataRequests.available === false
        ? []
        : responseNotices(invState.threads, invState.investigations, requests ?? [], userName, waitTimelines),
    [invState.threads, invState.investigations, requests, userName, waitTimelines, dataRequests.available],
  );
  // Answers for you (proposal 1): every answer not yet marked seen, one per
  // answer. Triage's list, its Responded count and this tab badge read it.
  const answers = useMemo(
    () =>
      dataRequests.available === false
        ? []
        : answersForYou(requests ?? [], invState.investigations, invState.threads),
    [requests, invState.investigations, invState.threads, dataRequests.available],
  );
  const respondedOnTriage = answers.length;

  // One answer open in the drawer, named by ?answer=<request id> so the bell
  // and every door page can open it, and the back button closes it.
  const [searchParams, setSearchParams] = useSearchParams();
  const answerId = searchParams.get('answer');
  const openRow = useMemo(() => {
    const request = answerId ? (requests ?? []).find((r) => r.id === answerId) : undefined;
    return request ? answerRow(request, invState.investigations, invState.threads) : null;
  }, [answerId, requests, invState.investigations, invState.threads]);
  const openAnswer = (requestId: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('answer', requestId);
      return next;
    });
  const closeAnswer = () =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('answer');
      return next;
    });

  const ctx: QualityTriageContext = {
    invState,
    userName,
    readOnly,
    bumpSeen: () => setSeenTick((t) => t + 1),
    dataRequests,
    deliveryOutcomes,
    responses,
    answers,
    openAnswer,
    followups,
    foundAnswers,
  };

  // Pending (kanban 43160d8b): how many promises are still to come, and how
  // many answers Merlin wrote that have not been read here yet.
  const pendingOpen = useMemo(() => openFollowups(followups.rows).length, [followups.rows]);
  const pendingUnread = useMemo(
    () => unseenAnswerCount(followups.rows, followupSeenSnapshot()),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seenTick re-reads localStorage seen-marks
    [followups.rows, seenTick],
  );

  // Merlin's drafts waiting for a Send, across items (UX round 2026-10-20):
  // the same count the Drafts to send page shows.
  const draftCount = useMemo(
    () =>
      dataRequests.available === false
        ? 0
        : draftQuestionCount(draftItems(requests ?? [], invState.threads, invState.investigations, userName)),
    [requests, invState.threads, invState.investigations, userName, dataRequests.available],
  );

  const moreEntries: MoreEntry[] = [
    {
      to: 'routed',
      label: 'Routed away',
      count: alerts.routedAway,
      alarm: undelivered > 0 ? `${undelivered} not delivered` : null,
      alarmCount: undelivered,
    },
    {
      to: 'drafts',
      label: 'Drafts to send',
      count: draftCount,
      notice: draftCount > 0 ? `${draftCount} to send` : null,
    },
    // The briefs route is gated on signals:read; without the grant the tab
    // used to bounce the user to Customers. Don't offer it.
    ...(hasScope('signals:read') ? [{ to: 'briefs', label: 'Daily briefs' }] : []),
  ];
  const cameFrom = isWorkspacePath(pathname) ? originFromState(state) : null;
  const door = pathname.replace(/^(.*\/quality-copilot)(\/.*)?$/, '$1');
  const rest = pathname.slice(door.length).replace(/^\//, '');
  const moreActive = cameFrom
    ? cameFrom === 'routed' || cameFrom === 'drafts'
      ? cameFrom
      : null
    : isWorkspacePath(pathname)
      ? null
      : (moreEntries.find((e) => rest === e.to || rest.startsWith(`${e.to}/`))?.to ?? null);
  const onAsk = rest === 'ask' || rest.startsWith('ask/');

  // On an item's workspace, the tab it was opened from stays lit (a case
  // opened from Done lit Cases, fan-out finding 12). Elsewhere: the route.
  // Lit and aria-current both follow this, never focus or hover: a keyboard
  // tester saw Cases keep its selected look while Triage had focus
  // (proposal 9), because NavLink's own aria-current disagreed with the lit
  // tab and hover looked the same as selected.
  const tabOn = (tab: DoorTab): boolean =>
    cameFrom
      ? cameFrom === tab
      : tab === 'triage'
        ? rest === ''
        : rest === tab || rest.startsWith(`${tab}/`);
  const tabProps = (tab: DoorTab) => {
    const on = tabOn(tab);
    return {
      className: `${styles.tab} ${on ? styles.tabActive : ''}`,
      'aria-current': on ? ('page' as const) : undefined,
      // The recorder names the tab by this and records which one, not the
      // label with its live counts ("Triage 22 updated") (kanban 68b23ba8).
      'data-action': 'qcp_door_tab',
      'data-record-value': 'tab',
      'data-value': tab,
    };
  };

  // The keyboard layer (proposal 9): j / k / Enter, o s d n a, /, g t / g c /
  // g d, Esc, and ? for this legend.
  const [legendOpen, setLegendOpen] = useState(false);
  useDoorShortcuts({
    goTab: (tab) => navigate(tab === 'triage' ? '.' : tab),
    legendOpen,
    setLegendOpen,
  });
  const toggleLegend = (open: boolean, via: 'hint' | 'close') => {
    trackEvent('action', 'shortcut_legend_toggle', { open, via });
    setLegendOpen(open);
  };

  // The bar must stay on one line at 1280px even when every tab carries its
  // badges and More names its page. When the tabs would overflow, the two
  // longest red badges drop their trailing words ("12 new", "13 updated");
  // their tooltips keep the full sentence. Measured before paint, again
  // whenever the badges change or the window is resized.
  const navRef = useRef<HTMLElement | null>(null);
  const fitKey = `${JSON.stringify(alerts)}|${moreActive ?? ''}|${undelivered}|${respondedOnTriage}|${pendingOpen}|${pendingUnread}`;
  const [fit, setFit] = useState<{ key: string; compact: boolean }>({ key: '', compact: false });
  const compact = fit.key === fitKey && fit.compact;
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav || fit.key === fitKey) return;
    setFit({ key: fitKey, compact: nav.scrollWidth > nav.clientWidth + 1 });
  }, [fit.key, fitKey]);
  useEffect(() => {
    const remeasure = () => setFit((f) => ({ ...f, key: '' }));
    window.addEventListener('resize', remeasure);
    return () => window.removeEventListener('resize', remeasure);
  }, []);

  return (
    <QualityReadOnlyContext.Provider value={readOnly}>
    <div className={styles.shell} data-read-only={readOnly || undefined}>
      {readOnly && (
        <p className={styles.readOnlyBanner} role="status" data-testid="read-only-banner">
          <strong>Read-only view of {desk?.home_name ?? 'the quality desk'}&rsquo;s desk.</strong>{' '}
          Look at any case, thread or clock and ask Merlin about it; changes are made by the
          quality desk. To get something changed, tell them, or answer the ask you were sent.
        </p>
      )}
      <header className={styles.bar}>
        {/* No "Quality · Merlin" title here any more (tabs round): the portal's
            top bar already shows "Merlin QUALITY" lit, with the same mark. */}
        <nav className={styles.tabs} aria-label="Quality Triage sections" ref={navRef}>
          <Link to="." {...tabProps('triage')}>
            Triage
            {alerts.queueCount > 0 && (
              <span className={styles.count}>{alerts.queueCount}</span>
            )}
            {/* Amber, in words (proposal 10): red is kept for overdue and
                not delivered. */}
            {alerts.queueUnseen > 0 && (
              <span
                className={styles.alertChanged}
                title={`${alerts.queueUnseen} thread${alerts.queueUnseen > 1 ? 's' : ''} updated since you last looked`}
              >
                {changedSinceWords(alerts.queueUnseen, compact)}
              </span>
            )}
            {/* Answers not yet marked seen (proposal 1): opens Triage's
                Responded filter, which lists them. */}
            {respondedOnTriage > 0 && (
              <span
                className={`${styles.responded} ${styles.alertLink}`}
                data-testid="tab-responded"
                data-action="qcp_door_tab_badge"
                data-record-value="badge"
                data-value="responded"
                title={`${respondedOnTriage} answer${respondedOnTriage > 1 ? 's' : ''} for you not marked seen yet. Click to show them.`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate({ pathname: '.', search: '?show=responded' });
                }}
              >
                {respondedOnTriage} responded
              </span>
            )}
          </Link>
          <Link to="cases" {...tabProps('cases')}>
            Cases
            {alerts.casesActive > 0 && (
              <span className={`${styles.count} ${styles.countMuted}`}>
                {alerts.casesActive} open
              </span>
            )}
            {alerts.casesUnseen > 0 && (
              <span
                className={`${styles.alert} ${styles.alertLink}`}
                data-action="qcp_door_tab_badge"
                data-record-value="badge"
                data-value="cases_new_answers"
                title={`${alerts.casesUnseen} case${alerts.casesUnseen > 1 ? 's' : ''} with a new answer to review. Click to show only those.`}
                // Opens Cases showing only the ones someone answered on
                // (waiting filter round), like Done's badge does.
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate('cases?show=responded');
                }}
              >
                {alerts.casesUnseen} new
                {compact ? '' : alerts.casesUnseen > 1 ? ' answers' : ' answer'}
              </span>
            )}
          </Link>
          {/* Scott (2026-09-08): "I don't see the Done category" — closed
              threads had been parked under Routed away. Their own tab. */}
          <Link to="done" {...tabProps('done')}>
            Done
            {alerts.doneCount > 0 && (
              <span className={`${styles.count} ${styles.countMuted}`}>
                {alerts.doneCount}
              </span>
            )}
            {/* The grey "16 notes added after closing" badge is gone (UX round
                2026-10-13): explorers in three rounds could not say what it
                meant, and it counted Merlin's housekeeping on closed items.
                Done keeps its own "new notes" filter on the page. */}
          </Link>
          {/* Pending (kanban 43160d8b): what Jess asked Merlin to watch or
              come back on. Hidden when the deploy has no follow-ups API. */}
          {followups.available && (
            <Link to="pending" {...tabProps('pending')} data-testid="tab-pending">
              Pending
              {pendingOpen > 0 && (
                <span className={`${styles.count} ${styles.countPending}`} data-testid="tab-pending-count">
                  {pendingOpen}
                </span>
              )}
              {pendingUnread > 0 && (
                <span
                  className={styles.responded}
                  data-testid="tab-pending-answered"
                  title={`${pendingUnread} answer${pendingUnread > 1 ? 's' : ''} from Merlin not read yet`}
                >
                  {pendingUnread} answered
                </span>
              )}
            </Link>
          )}
          <QualityMoreMenu entries={moreEntries} activeTo={moreActive} />
        </nav>
        {/* A question not tied to one item (tabs round, finding 3). Every
            item and case has its own Merlin pane, so this is no longer a tab;
            the portal's top-bar "Merlin" lands on Triage, not here. */}
        <Link
          to="ask"
          className={`${styles.askMerlin} ${onAsk ? styles.askMerlinOn : ''}`}
          aria-current={onAsk ? 'page' : undefined}
          data-action="qcp_ask_merlin_open"
          title="Ask Merlin a general question, not about one item"
        >
          <MerlinMark
            accent={MERLINS.find((m) => m.id === 'quality')?.accent ?? '#a855f7'}
            size={18}
          />
          Ask Merlin
        </Link>
        <QualitySearch
          threads={invState.threads}
          investigations={invState.investigations}
          userName={userName}
        />
        <button
          type="button"
          className={styles.shortcutHint}
          aria-expanded={legendOpen}
          data-testid="shortcut-hint"
          onClick={() => toggleLegend(!legendOpen, 'hint')}
          title="Keyboard shortcuts"
        >
          Press <kbd>?</kbd> for shortcuts
        </button>
        {!readOnly && (
          <button
            type="button"
            className={styles.newIssue}
            onClick={() => navigate('ask?intent=report-issue')}
            title="Report a new quality issue — Merlin walks you through it"
          >
            + New quality issue
          </button>
        )}
      </header>
      <DeskOwnerLine desk={desk} />
      {/* What the team behind this door is for (kanban 16a22d06), read from
          the roadmap's team registry — no copy of the Sigma definition here. */}
      <TeamBriefLine enabled={!!userId} />
      {/* PortalLayout gives copilot routes a FIXED-height, overflow-hidden
          canvas (.layoutFlush) — so the shell owns the scrolling: list tabs
          scroll here; the Ask tab's chat fills the height and scrolls itself. */}
      <div className={styles.content} ref={contentRef}>
        <Outlet context={ctx} />
      </div>
      {legendOpen && <ShortcutLegend onClose={() => toggleLegend(false, 'close')} />}
      {openRow && (
        <AnswerDrawer
          key={openRow.request.id}
          row={openRow}
          investigations={invState.investigations}
          userName={userName}
          onClose={closeAnswer}
          onUpdated={dataRequests.applyUpdate}
          onCaseUpdated={invState.applyUpdate}
        />
      )}
    </div>
    </QualityReadOnlyContext.Provider>
  );
}
